import { gameState } from '../state/gameState.js';
import { computeScene, phaseInfo } from './animalBehavior.js';
import { AnimalSprite } from './AnimalSprite.js';
import { openAnimalActions } from './animalActions.js';
import { ySortDepth } from './ySort.js';
import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { serverNow } from '../state/serverClock.mjs';

export class AnimalsController {
  constructor(scene, opts) {
    this.scene = scene;
    this.sceneId = opts.sceneId;
    this.ownerId = opts.ownerId ?? null;
    // world units; scene.scale.* is the retina canvas size (2x)
    this.xBounds = opts.xBounds ?? [120, GAME_WIDTH - 120];
    // Visible vertical band for an animal's centre: the sprite is ~100px (cow 150), so keep
    // the feet above the bottom edge. Every client must pass the same bounds for a scene
    // (computeScene is deterministic), hence one default here rather than per-scene values.
    this.yBounds = opts.yBounds ?? [80, GAME_HEIGHT - 80];
    this.getPlayers = opts.getPlayers ?? (() => []);
    // World-data source: gameState.raw for owner scenes; a visit scene passes the host's view.
    this.getWorld = opts.getWorld ?? (() => ({
      animals: gameState.raw?.animals ?? [],
      placements: gameState.raw?.placements ?? [],
      catalog: gameState.raw?.catalog,
    }));
    this.sprites = new Map();          // animal id -> AnimalSprite
    this._cycleStartedMs = 0;
    this._syncCycleAnchor();
  }

  _syncCycleAnchor() {
    const iso = gameState.raw?.time?.cycle_started_at_iso;
    this._cycleStartedMs = iso ? new Date(iso).getTime() : 0;
  }

  // Animals whose buda lives in this scene, each with its resolved buda position.
  _sceneAnimals() {
    const { animals, placements } = this.getWorld();
    const out = [];
    for (const a of animals ?? []) {
      const buda = (placements ?? []).find((p) => p.id === a.buda_placement_id);
      if (!buda || buda.scene !== this.sceneId) continue;
      out.push({ ...a, buda: { x: buda.x, y: buda.y } });
    }
    return out;
  }

  update(dt) {
    this._syncCycleAnchor();
    const animals = this._sceneAnimals();

    // Drop sprites whose animal/buda is gone.
    const live = new Set(animals.map((a) => a.id));
    for (const [id, sp] of this.sprites) {
      if (!live.has(id)) { sp.destroy(); this.sprites.delete(id); }
    }

    const states = computeScene({
      nowMs: serverNow(),
      cycleStartedMs: this._cycleStartedMs,
      sceneId: this.sceneId,
      animals,
      xBounds: this.xBounds,
      yBounds: this.yBounds,
      players: this.getPlayers(),
      ownerId: this.ownerId,
      phaseOverride: gameState._devPhase ?? null,
    });

    for (const a of animals) {
      let sp = this.sprites.get(a.id);
      if (!sp) {
        sp = new AnimalSprite(this.scene, a.buda.x + 60, a.buda.y, a);
        // Look up the LIVE animal at click time — the captured `a` is a snapshot from
        // creation, so its pending_count/hunger would be stale (e.g. an egg laid later
        // wouldn't be collectable).
        sp.onClick(() => {
          const cur = (this.getWorld().animals ?? []).find((x) => x.id === a.id) ?? a;
          this.scene._onAnimalClick?.(cur);
        });
        this.sprites.set(a.id, sp);
      }
      sp.update(a, this.getWorld().catalog);   // refresh badge data (happiness/hungry/product)
      const rs = states.get(a.id);
      if (rs) {
        sp.applyRenderState(rs, dt);
        if (!rs.hidden) sp.setDepth(ySortDepth(rs.y + 62));
      }
    }
  }

  // Is the animal in this buda currently asleep (hidden) in this scene?
  _hiddenAnimalForBuda(budaPlacement) {
    const a = (this.getWorld().animals ?? []).find((x) => x.buda_placement_id === budaPlacement.id);
    if (!a) return null;
    const phase = gameState._devPhase ?? phaseInfo(serverNow(), this._cycleStartedMs).phase;
    return phase === 'night' ? a : null;
  }

  // Route a click on a buda to its sleeping occupant (collect only) at night.
  attachBudaClick(budaSprite, budaPlacement) {
    budaSprite.on('pointerup', () => {
      const a = this._hiddenAnimalForBuda(budaPlacement);
      if (a) openAnimalActions(this.scene, a, { nightCollectOnly: true });
    });
  }

  destroy() {
    for (const sp of this.sprites.values()) sp.destroy();
    this.sprites.clear();
  }
}
